import { useContext, useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import styled from "styled-components";
import type { ControlcenterAPIInterfaceExternal } from "@dealer-center/ddc-pages-controlcenter-interface";
import type { decodedJwtAndPermissions } from "@dealer-center/ddc-api-user-interface";
import { IncentivesManagerHeader } from "./components/IncentivesManagerHeader";
import { MessagePage } from "@dealer-center/core-custom-components";
import { Locale } from "@dealer-center/ddc-core-utils";
import { getEmptyIncentive, getTabStates } from "./utils/utils";
import { Alert, Modal } from "@dealer-center/core-components";
import TableAttributesPopup from "./components/TableAttributesPopup";
import { TabContainer } from "./components/TabContainer";
import { TableNavigation } from "./components/TableNavigation";
import { Slidebar } from "./components/Slidebar";
import { FiltersContext } from "./providers/FiltersProvider";
import { PreferencesPopup } from "./components/PreferencesPopup";
import { EditIncentivePopup } from "./components/EditIncentivePopup";
import type { IncentivesRecord } from "@dealer-center/ddc-pages-incentives-manager-interface";
import type { EditIncentivesDisableFlags } from "@dealer-center/ddc-pages-incentives-manager-interface";
import { ModalConfirm } from "./components/ModalConfirm";
import { PreferencesContext } from "./providers/PreferencesProvider";

const StyledDiv = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  height: calc(100% + 16px);
  margin: -16px;
`;

const PaddedModalContent = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  overflow: hidden;
`;

const ContentFrame = styled.div`
  position: relative;
  display: flex;
  flex-direction: row;
  height: 100%;
`;

const TableContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  overflow: auto;
`;

export const IncentivesManager = () => {
  const [locale, setLocale] = useState("en-US");
  const {
    state,
    dispatch: dispatchFilters,
    isMakeLocked
  } = useContext(FiltersContext);
  const {
    incentives,
    isLoading,
    isNavLoading,
    error,
    filters,
    navtreeData,
    accountMappings
  } = state;

  const [editIncentivePopup, setEditIncentivePopup] = useState({
    isShowing: false,
    type: "CREATE_INCENTIVE",
    incentive: getEmptyIncentive()
  });

  const [isAttributesModalShowing, setIsAttributesModalShowing] =
    useState(false);
  const [isPreferencesModalShowing, setIsPreferencesModalShowing] =
    useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const { ccUserInfo } = useOutletContext<{
    ccUserInfo: ControlcenterAPIInterfaceExternal;
    userEntity: decodedJwtAndPermissions;
  }>();
  const [tabIndex, setTabIndex] = useState(-1);
  const [hasEditChanges, setEditChanges] = useState(false);

  const { preferences } = useContext(PreferencesContext);
  const defaultContent = Array.isArray(preferences.contentDefaults)
    ? preferences.contentDefaults
    : [];

  const showIncentivePopup = (
    isShowing: boolean,
    type = "CREATE_INCENTIVE",
    incentive: IncentivesRecord = getEmptyIncentive()
  ) => {
    setEditChanges(false);
    setEditIncentivePopup({ isShowing, type, incentive });
  };

  const getEditIncentivesDisableFlags = (
    incentive: IncentivesRecord,
    type: string
  ): EditIncentivesDisableFlags => {
    const flags: EditIncentivesDisableFlags = {
      disableTypeEditing: false,
      publisherMode: false,
      published: false,
      readOnly: false,
      disableDistributionLists: false,
      dealerStackOptionEnabled: false,
      dealerStackOptionChecked: false,
      hideDistributionLists: incentive.mappedByZipCode || false
    };

    if (incentives && type !== "CREATE_INCENTIVE") {
      // CC6 ref: https://ghe.coxautoinc.com/DDC-Dont-Panic/control-center-ui/blob/74b196cdcfa0dde3d5ec05c065a9df346f401356/plugins/globalvehiclemanager/htdocs/globalvehiclemanager/js/ext-incentive-manager.js#L1395
      flags.disableTypeEditing = tabIndex !== 0 || type === "CLONE_INCENTIVE"; // Drafts are always 1st tab
      flags.published =
        getTabStates(
          incentives,
          accountMappings?.hasAnyUpstreamCompliance || false
        ).findIndex(state => state.logicalState === "PUBLISHED") === tabIndex;
      if (type === "OPEN_INCENTIVE") {
        flags.readOnly = true;
        flags.disableDistributionLists = true;
      } else {
        flags.publisherMode =
          incentive.creatorAccountId !== ccUserInfo?.accountId;
        flags.readOnly =
          flags.publisherMode &&
          incentive.accountMap.status === "STATUS_ACTREQ_COMP";
        if (flags.readOnly) {
          flags.publisherMode = false; // readOnly overrides publisherMode
        }
        // Confusing CC6 logic:
        // Ref: https://ghe.coxautoinc.com/DDC-Dont-Panic/control-center-ui/blob/74b196cdcfa0dde3d5ec05c065a9df346f401356/plugins/globalvehiclemanager/htdocs/globalvehiclemanager/js/ext-add-incentive-dialog.js#L164
        if (flags.published || flags.readOnly) {
          flags.disableTypeEditing = true;
          flags.disableDistributionLists = flags.readOnly;
        }
        if (incentive?.applicableCondition === "CPO" /*  && !hasCPO */) {
          // this.isCPO = true;
          flags.publisherMode = !flags.readOnly;
        }
        if (flags.publisherMode) {
          flags.readOnly = true;
        }
      }
    }
    // ref: https://ghe.coxautoinc.com/DDC-Dont-Panic/control-center-ui/blob/74b196cdcfa0dde3d5ec05c065a9df346f401356/plugins/globalvehiclemanager/htdocs/globalvehiclemanager/js/ext-add-incentive-dialog.js#L107
    if (type === "CREATE_INCENTIVE" || type === "CLONE_INCENTIVE") {
      flags.dealerStackOptionEnabled = true;
    } else if (type === "EDIT_INCENTIVE") {
      flags.dealerStackOptionEnabled =
        incentive.creatorAccountId === ccUserInfo?.accountId;
    }
    if (incentive?.accountMap.dealerStackOption) {
      flags.dealerStackOptionChecked = true;
    }
    return flags;
  };

  useEffect(() => {
    const titleLocale = Locale.getText("INCENTIVES_MANAGER");
    document.title =
      typeof titleLocale === "string" ? titleLocale : "Incentives Manager";
  }, []);

  useEffect(() => {
    const newLocale = Locale.getLocale();

    if (locale !== newLocale) {
      Locale.change(newLocale);
      setLocale(newLocale);
    }
  }, [locale]);

  if (error) {
    return (
      <Alert role="alert" sx={{ marginBottom: "16px" }} type="error">
        {Locale.getText("ERROR_LOADING_INCENTIVES") as string}
      </Alert>
    );
  }

  if (isLoading) {
    return (
      <div data-testid="ddc-page">
        <MessagePage
          actionName={"Loading incentives"}
          message={Locale.getText("LOADING_INCENTIVES") as string}
        />
      </div>
    );
  }

  return (
    <StyledDiv>
      <Modal
        header={Locale.getText("TABLE_ATTRIBUTES")}
        show={isAttributesModalShowing}
        size={"large"}
        onHide={() => {
          setIsAttributesModalShowing(false);
        }}
      >
        <TableAttributesPopup
          onCancel={() => {
            setIsAttributesModalShowing(false);
          }}
        />
      </Modal>
      <Modal
        className="incentives-manager-popup"
        header={Locale.getText("PREFERENCES")}
        show={isPreferencesModalShowing}
        size={"large"}
        onHide={() => setIsPreferencesModalShowing(false)}
      >
        <PreferencesPopup
          onCancel={() => {
            setIsPreferencesModalShowing(false);
          }}
          onSave={() => {
            setIsPreferencesModalShowing(false);
          }}
        />
      </Modal>
      <Modal
        className="incentives-manager-popup"
        header={Locale.getText(editIncentivePopup.type)}
        show={editIncentivePopup.isShowing}
        size={"large"}
        onHide={() => {
          if (hasEditChanges) {
            setShowConfirmDialog(true);
          } else {
            showIncentivePopup(false);
          }
        }}
      >
        <PaddedModalContent>
          <EditIncentivePopup
            asAccount={ccUserInfo.accountId}
            asUser={ccUserInfo.userId}
            currentFilters={filters}
            defaultSelectedMake={filters.make || ""}
            getDisableFlags={getEditIncentivesDisableFlags}
            hasEditChanges={hasEditChanges}
            incentive={editIncentivePopup.incentive}
            isMakeLocked={isMakeLocked}
            navtreeData={navtreeData}
            type={editIncentivePopup.type}
            onCancel={() => {
              if (hasEditChanges) {
                setShowConfirmDialog(true);
              } else {
                showIncentivePopup(false);
              }
            }}
            onChange={hasChanges => {
              setEditChanges(hasChanges);
            }}
            onSave={() => {
              showIncentivePopup(false);
            }}
          />
        </PaddedModalContent>
        <ModalConfirm
          cancelText={Locale.getText("QUIT") as string}
          confirmText={Locale.getText("KEEP_EDITING") as string}
          message={Locale.getText("QUIT_EDITING_CONFIRMATION") as string}
          open={showConfirmDialog}
          onClose={() => {
            showIncentivePopup(false);
            setShowConfirmDialog(false);
          }}
          onConfirm={() => {
            setShowConfirmDialog(false);
          }}
        />
      </Modal>
      <ContentFrame>
        <Slidebar>
          <TableNavigation
            accountMakes={accountMappings?.accountMakes || []}
            filters={filters}
            incentives={incentives}
            isLoading={isNavLoading}
            navtreeData={navtreeData}
            setFilters={newFilters => {
              dispatchFilters({ type: "SET_FILTERS", filters: newFilters });
            }}
          />
        </Slidebar>
        <TableContainer>
          <IncentivesManagerHeader
            isCreateIncentiveDisabled={
              !ccUserInfo?.userPermissions?.includes(
                "INCENTIVESMANAGER.MANUALINSERT"
              ) || isMakeLocked(filters.make || "")
            }
            onAttributesClick={() => setIsAttributesModalShowing(true)}
            onCreateIncentiveClick={() => {
              const newIncentive = getEmptyIncentive(
                filters,
                ccUserInfo?.accountId,
                defaultContent
              );
              // Always set condition to "NEW" when creating a new incentive
              newIncentive.applicableCondition = "NEW";
              showIncentivePopup(true, "CREATE_INCENTIVE", newIncentive);
            }}
            onPreferencesClick={() => setIsPreferencesModalShowing(true)}
          />
          <TabContainer
            hasAnyUpstreamCompliance={
              accountMappings?.hasAnyUpstreamCompliance || false
            }
            incentives={incentives}
            setTabIndex={setTabIndex}
            showIncentivePopup={showIncentivePopup}
            tabIndex={tabIndex}
          />
        </TableContainer>
      </ContentFrame>
    </StyledDiv>
  );
};
